import { Home, RefreshCw } from 'lucide-react'
import { CustomButton } from '../ui/custom-ui/CustomButton'
import { SadHedgeHog } from '../BenepicksComponents/SadHedgeHog'
import Card from '../Card'
import { cn } from '@/lib/utils'

export interface ErrorFallbackProps {
  error?: Error
  resetErrorBoundary?: () => void
}

export interface ErrorFallbackBaseProps extends ErrorFallbackProps {
  title?: string
  description?: string
  primaryColorClass?: string
  showHomeButton?: boolean
  showResetButton?: boolean
  homeUrl?: string
}

export const ErrorFallbackBase = ({
  error,
  resetErrorBoundary,
  title = 'Something went wrong',
  description = 'We found an unexpected error. Please try again.',
  primaryColorClass = 'primaryEmployee',
  showHomeButton = true,
  showResetButton = true,
  homeUrl = '/',
}: ErrorFallbackBaseProps) => {
  const handleHomeClick = () => {
    window.location.assign(homeUrl)
  }

  const handleReset = () => {
    resetErrorBoundary?.()
  }

  return (
    <div className="flex items-center justify-center p-4 min-h-[calc(100vh-64px)]">
      <div className="max-w-md w-full">
        <Card className="bg-white rounded-lg p-8 text-center">
          <div className="mb-6">
            <div
              className={cn(
                'mx-auto w-28 h-28 rounded-full flex items-center justify-center',
                `bg-${primaryColorClass}-100`
              )}
            >
              <SadHedgeHog size="w-28 h-28" />
            </div>
          </div>

          <h1 className="text-xl font-sans font-semibold text-black-text mb-3">
            {title}
          </h1>

          <p className="text-gray-700 font-body mb-6 leading-relaxed">
            {description}
          </p>

          {/* Error details in development */}
          {process.env.NODE_ENV !== 'production' && error && (
            <div className="mb-6 p-3 bg-red-50 rounded-md text-center">
              <p className="text-xs font-mono text-red-800 break-all">
                {error.message}
              </p>
            </div>
          )}

          <div className="space-y-3">
            {showResetButton && resetErrorBoundary && (
              <CustomButton
                onClick={handleReset}
                className={cn(
                  'w-full',
                  `bg-${primaryColorClass}-600 hover:bg-${primaryColorClass}-700 text-white`
                )}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </CustomButton>
            )}
            {showHomeButton && (
              <CustomButton
                onClick={handleHomeClick}
                variant="outline"
                className={cn(
                  'w-full',
                  `border-${primaryColorClass}-300 text-${primaryColorClass}-700 hover:bg-${primaryColorClass}-50`
                )}
              >
                <Home className="w-4 h-4" />
                Go to Home
              </CustomButton>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
